import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";
import {
  registerGlobalForbiddenSetter,
  unregisterGlobalForbiddenSetter,
} from "./globalForbidden";

type ForbiddenContextType = {
  /** True when the API answered 403 for the current page. */
  isForbidden: boolean;
  /** Clear the forbidden state (e.g. when navigating to another route). */
  clearForbidden: () => void;
};

const ForbiddenContext = createContext<ForbiddenContextType | undefined>(
  undefined,
);

export function ForbiddenProvider({ children }: PropsWithChildren) {
  const [isForbidden, setForbidden] = useState(false);

  const clearForbidden = useCallback(() => setForbidden(false), []);

  useEffect(() => {
    registerGlobalForbiddenSetter(() => setForbidden(true));
    return () => unregisterGlobalForbiddenSetter();
  }, []);

  const value = useMemo(
    () => ({ isForbidden, clearForbidden }),
    [isForbidden, clearForbidden],
  );

  return (
    <ForbiddenContext.Provider value={value}>
      {children}
    </ForbiddenContext.Provider>
  );
}

export function useForbidden() {
  const context = useContext(ForbiddenContext);
  if (!context) {
    throw new Error("useForbidden must be used within ForbiddenProvider");
  }
  return context;
}
